import React, {FC, useMemo} from 'react';

import Box from '@material-ui/core/Box';
import {withStyles, BoxProps} from '@material-ui/core';

import MyTop10GamesStatisticsCard from './MyTop10GamesStatisticsCard';
import MyTop10GamesPanelTrend from './MyTop10GamesPanelTrend';
import DataUnavailableTooltip from '../DataUnavailableTooltip';
import DataUnavailableBox from '../DataUnavailableBox';

import {getRank} from '../Utils';
import {GameDataItem} from '../../../types';

const TOP_GAMES_LIMIT = 10;

const DataUnavailableWrapper = withStyles<string, undefined, BoxProps>(theme => ({
    root: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'stretch',
        width: '100%',
        height: '540px',
        backgroundColor: theme.palette.secondary.main
    }
}))(Box);

export interface MyTop10GamesListProps {
    list: GameDataItem[];
    formatter: (value: number | string) => number | string;
    tooltipTitle: string;
    tooltipDescription: string;
    color?: string;
}

export interface RankedListProps extends GameDataItem {
    bar: number;
    rank?: number;
}

const MyTop10GamesList: FC<MyTop10GamesListProps> = ({
    list,
    formatter,
    tooltipTitle,
    tooltipDescription,
    color
}: MyTop10GamesListProps) => {
    const maxValue = list.length && list[0].value;

    const rankedList: RankedListProps[] = useMemo(() => {
        return (
            (list.length &&
                list.map(list => ({
                    ...list,
                    bar: getRank(maxValue as number, list.value as number)
                }))) ||
            []
        );
    }, [list]);

    if (!rankedList.length) {
        return (
            <DataUnavailableWrapper>
                <DataUnavailableBox
                    title={tooltipTitle}
                    tooltip={
                        <DataUnavailableTooltip
                            title={tooltipTitle}
                            description={tooltipDescription}
                        />
                    }
                />
            </DataUnavailableWrapper>
        );
    }

    const emptyCards = Array(TOP_GAMES_LIMIT - rankedList.length).fill({value: 0});

    return (
        <>
            {rankedList
                .concat(emptyCards)
                .map(({label, value, trend, rank, bar, creativeIslandCode}, index) => (
                    <MyTop10GamesStatisticsCard
                        key={`${index}-${label}`}
                        rank={rank || index + 1}
                        title={label}
                        value={formatter(value)}
                        code={creativeIslandCode}
                        trend={trend}
                        bar={bar}
                        color={color}
                        tooltip={
                            !value &&
                            value !== 0 && (
                                <Box ml={4}>
                                    <DataUnavailableTooltip
                                        title={tooltipTitle}
                                        description={tooltipDescription}
                                    />
                                </Box>
                            )
                        }>
                        {value !== 0 && <MyTop10GamesPanelTrend value={trend} />}
                    </MyTop10GamesStatisticsCard>
                ))}
        </>
    );
};

export default MyTop10GamesList;
